import { formatValue } from "../utils/helpers";
import metricMetadataIcon from '../assets/metric-metadata-icon.svg';
import { useEffect, useRef, useState } from "react";
import MetricMetadataModal from "./MetricMetadataModal";
import { DisaggregatedMetric, DistributionData, GeoDetails, GeoTag, MetricMetadata, RawIndicatorData } from "../services/types";
import { fetchDistributionData, fetchIndicatorBinCount, fetchMetricMetadata, indexMetricIds } from "../utils/dashboardUtils";

interface MetricCardProps {
    metric: DisaggregatedMetric;
    width: string;
    disaggData: DisaggregatedMetric[] | null;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    svg?: string;
    showMetadataIcon?: boolean;
};

const MetricCard = ({ metric, disaggData, geoDetails, geoTag, selectionActive, svg, showMetadataIcon = true }: MetricCardProps) => {
    const [showMetadata, setShowMetadata] = useState(false);
    const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
    const [distributionData, setDistributionData] = useState<RawIndicatorData[] | null>(null);
    const [countyDistributionData, setCountyDistributionData] = useState<DistributionData[] | null>(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Destructure metric properties
    const { id, name, versions } = metric;
    const { value, unit, unit_descriptor, percent_calc } = versions[0];

    const val = unit === "ratio" ? `${percent_calc?.n}/${percent_calc?.d}` : value;

    // Index metric?
    const isIndexMetric = indexMetricIds.includes(metric.id);

    // Refs for metadata modal
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const modalId = `metric-metadata-modal-${id ?? 'main'}`;
    const clickDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchCache = useRef<Record<string, Promise<any> | undefined>>({});

    function fetchWithCache<T>(key: string, fetchFn: () => Promise<T>): Promise<T> {
        if (fetchCache.current[key]) {
            // Return ongoing request if already in-flight
            return fetchCache.current[key] as Promise<T>;
        }

        const promise = fetchFn().finally(() => {
            delete fetchCache.current[key];
        });

        fetchCache.current[key] = promise;
        return promise;
    }

    // Prefetch on mount (throttled sequentially)
    useEffect(() => {
        // Only fetch if not already cached or loaded
        if (!metadata) {
            fetchWithCache(`metadata-${id}`, () => fetchMetricMetadata(id))
            .then(res => res?.data && setMetadata(res.data))
            .catch(console.error);
        }

        if (!distributionData) {
            const distKey = `distData-${id}-${geoDetails.map(d => d.lod).join(',')}-${geoTag?.type}-${geoTag?.value}`;
            fetchWithCache(distKey, () => fetchIndicatorBinCount(id, geoDetails, geoTag))
            .then(res => res && setDistributionData(res))
            .catch(console.error);
        }

        if (!countyDistributionData) {
            const countyKey = `countyDist-${id}-53033`;
            fetchWithCache(countyKey, () => fetchDistributionData(id, [], null))
            .then(res => res && setCountyDistributionData(res))
            .catch(console.error);
        }
    }, [id, geoDetails, geoTag]);

    const handleClick = async () => {
        if (clickDebounceRef.current) clearTimeout(clickDebounceRef.current);

        clickDebounceRef.current = setTimeout(async () => {
            setShowMetadata((prev) => !prev);
            setLoading(true);

            try {
            const [metadataResponse, distributionDataResponse, countyDistributionDataResponse] = await Promise.all([
                metadata
                ? Promise.resolve({ data: metadata })
                : fetchWithCache(`metadata-${id}`, () => fetchMetricMetadata(id)),

                distributionData
                ? Promise.resolve(distributionData)
                : fetchWithCache(`distData-${id}-${geoDetails.map(d => d.lod).join(',')}-${geoTag?.type}-${geoTag?.value}`, () =>
                    fetchIndicatorBinCount(id, geoDetails, geoTag)
                    ),

                countyDistributionData
                ? Promise.resolve(countyDistributionData)
                : fetchWithCache(`countyDist-${id}-53033`, () =>
                    fetchDistributionData(id, [], null)
                    ),
            ]);

            if (!metadata) setMetadata(metadataResponse?.data || null);
            if (!distributionData) setDistributionData(distributionDataResponse || null);
            if (!countyDistributionData) setCountyDistributionData(countyDistributionDataResponse || null);

            } catch (error) {
            const errorMessage = (error as Error).message || 'An unexpected error occurred.';
            console.error('Error fetching data:', error);
            setError(new Error(`Failed to load data: ${errorMessage}`));
            } finally {
            setLoading(false);
            }
        }, 150);
    };

    return (
        <div 
            className="relative overflow-hidden group hover:shadow-lg transition-all border rounded-xl bg-white" 
            style={{ 
                borderLeftWidth: isIndexMetric ? "4px" : undefined, borderLeftColor: isIndexMetric ? "#225591" : undefined,
                }}
        >
            <div className="p-3">
                <div className="flex items-start justify-between mb-1">
                    <div className="flex items-center gap-2">
                        {/* Metric Icon */}
                        { svg && <img src={svg} alt={`${name} icon`} className="w-12 h-12 text-blue-500" /> }
                        {/* Metric Value */}
                        <span className="text-[1.75rem] font-semibold leading-none text-lacey-blue">
                            {formatValue(val, unit, id)}
                        </span>
                    </div>

                    {/* Metadata Icon */}
                    {showMetadataIcon && 
                        <button
                            type="button"
                            onClick={handleClick}
                            aria-haspopup="dialog"
                            aria-expanded={showMetadata ? 'true' : 'false'}
                            aria-controls={modalId}
                            aria-label={`Additional information about ${name}`}
                            className="
                            inline-flex h-6 w-6 items-start justify-center rounded
                            transition-transform hover:scale-105 focus:scale-105
                            focus-visible:outline-none
                            focus-visible:ring-2 focus-visible:ring-offset-2
                            focus-visible:ring-neutral-900 focus-visible:ring-offset-white
                            dark:focus-visible:ring-white dark:focus-visible:ring-offset-neutral-900
                            "
                        >
                            <img src={metricMetadataIcon} alt="" aria-hidden="true" className="h-3 w-3" draggable="false" />
                        </button>
                    }
                </div>

                <div className="space-y-0.5">
                    <h3 className="text-sm font-semibold text-lacey-text">
                        {name}
                    </h3>
                    <p className="text-xs text-muted-foreground leading-tight">
                        {unit_descriptor}
                    </p>
                    {isIndexMetric &&
                        <div className="rounded-lg bg-lacey-blue h-4 w-10 px-1.5 text-[10px] text-white text-center">Index</div> 
                    }
                </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2258e5]/50 to-[#00ac4e]/50 opacity-0 group-hover:opacity-100 transition-opacity" />

            <MetricMetadataModal
                isOpen={showMetadata}
                onClose={handleClick}
                id={id}
                metadata={metadata}
                stateData={null}
                nationalData={null}
                disaggData={disaggData}
                distributionData={distributionData}
                countyDistributionData={countyDistributionData}
                selectionActive={selectionActive}
                // svg={svg}
                loading={loading}
                error={error}
                triggerRef={triggerRef}
                modalId={modalId}
            />
        </div>
    );
};


export default MetricCard;
